import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { tokenManager } from '../services/authApi'
import { extractUserFromJWT, decodeJWT, getTokenExpiration } from '../utils/jwtDecoder'
import { formatProfilePictureUrl } from '../utils/profilePictureHelper'
import { showError } from '../utils/showError'
import {
  PageHeader,
  Button,
  FormField,
  LoadingSpinner,
  ErrorMessage,
  Card,
  CardHeader,
  CardBody,
  Badge
} from '../components/common'

const Profile = () => {
  const { user } = useAuth()
  const [profileData, setProfileData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isEditing, setIsEditing] = useState(false)
  const [showJWTDebug, setShowJWTDebug] = useState(false)
  const [jwtData, setJwtData] = useState(null)
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: ''
  })

  useEffect(() => {
    loadProfileData()
  }, [])

  const loadProfileData = async () => {
    try {
      setLoading(true)
      setError(null)

      // Get current JWT token and decode it
      const token = tokenManager.getToken()
      if (token) {
        const decodedJWT = decodeJWT(token)
        const userFromJWT = extractUserFromJWT(token)

        setJwtData({
          decoded: decodedJWT,
          user: userFromJWT,
          tokenExpiration: getTokenExpiration(token),
          rawToken: token
        })

        // Use JWT data as primary source
        setProfileData(userFromJWT || user)
        setEditForm({
          firstName: userFromJWT?.firstName || user?.firstName || '',
          lastName: userFromJWT?.lastName || user?.lastName || '',
          email: userFromJWT?.email || user?.email || '',
          phoneNumber: userFromJWT?.phoneNumber || user?.phoneNumber || ''
        })
      } else {
        // Fallback to context user data
        setProfileData(user)
        setEditForm({
          firstName: user?.firstName || '',
          lastName: user?.lastName || '',
          email: user?.email || '',
          phoneNumber: user?.phoneNumber || ''
        })
      }
    } catch (err) {
      console.error('Failed to load profile:', err)
      setProfileData(user)
      setEditForm({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phoneNumber: user?.phoneNumber || ''
      })
      setError('Could not decode JWT token')
    } finally {
      setLoading(false)
    }
  }

  const handleEditToggle = () => {
    setIsEditing(!isEditing)
    if (!isEditing) {
      // Reset form when starting to edit
      setEditForm({
        firstName: profileData?.firstName || '',
        lastName: profileData?.lastName || '',
        email: profileData?.email || '',
        phoneNumber: profileData?.phoneNumber || ''
      })
    }
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleSaveProfile = async (e) => {
    e.preventDefault()
    try {
      setLoading(true)

      // Profile data is read-only from JWT — update local display only
      const updatedProfile = { ...profileData, ...editForm }
      setProfileData(updatedProfile)
      tokenManager.setUserProfile(updatedProfile)
      setIsEditing(false)

    } catch (err) {
      console.error('Profile update error:', err)
      showError(err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <LoadingSpinner />
  }

  const displayData = profileData || user

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile"
        subtitle="Manage your account information"
      />

      {error && (
        <ErrorMessage
          message={error}
          onDismiss={() => setError(null)}
        />
      )}

      <div className="max-w-4xl grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <Card className="text-center">
            <CardBody>
              <div className="mb-4">
                {displayData?.profilePictureUrl ? (
                  <img
                    src={formatProfilePictureUrl(displayData.profilePictureUrl)}
                    alt="Profile"
                    className="w-20 h-20 rounded-full mx-auto object-cover border-2 border-slate-200 shadow-sm"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'flex'
                    }}
                  />
                ) : null}
                <div
                  className="w-20 h-20 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mx-auto flex items-center justify-center text-white text-2xl font-bold"
                  style={{ display: displayData?.profilePictureUrl ? 'none' : 'flex' }}
                >
                  {displayData?.firstName?.[0] || displayData?.name?.[0] || displayData?.email?.[0] || 'U'}
                </div>
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-1">
                {displayData?.firstName && displayData?.lastName
                  ? `${displayData.firstName} ${displayData.lastName}`
                  : displayData?.name || 'User'
                }
              </h2>
              <p className="text-slate-500 mb-4">{displayData?.email}</p>

              <div className="space-y-3 text-sm mt-4">
                <div className="flex items-center justify-center space-x-2">
                  <Badge variant="success" size="small">Active</Badge>
                </div>

                <div className="flex items-center justify-center space-x-2 text-slate-600">
                  <span>📞 {displayData.phoneNumber}</span>
                  <Badge variant={displayData?.phoneNumberConfirmed ? 'success' : 'warning'} size="small">
                    {displayData?.phoneNumberConfirmed ? 'Verified' : 'Unverified'}
                  </Badge>
                </div>

                {displayData?.email && (
                  <div className="flex items-center justify-center space-x-2 text-slate-600">
                    <span>📧 {displayData.email}</span>
                    <Badge variant={displayData?.emailConfirmed ? 'success' : 'warning'} size="small">
                      {displayData?.emailConfirmed ? 'Verified' : 'Unverified'}
                    </Badge>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Profile Information */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader
              title="Profile Information"
              action={
                <Button
                  onClick={handleEditToggle}
                  variant="ghost"
                >
                  {isEditing ? 'Cancel' : 'Edit Profile'}
                </Button>
              }
            />
            <CardBody>
              {isEditing ? (
                <form onSubmit={handleSaveProfile} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      label="First Name"
                      name="firstName"
                      value={editForm.firstName}
                      onChange={handleInputChange}
                    />

                    <FormField
                      label="Last Name"
                      name="lastName"
                      value={editForm.lastName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <FormField
                    label="Email Address"
                    name="email"
                    type="email"
                    value={editForm.email}
                    onChange={handleInputChange}
                  />

                  <FormField
                    label="Phone Number"
                    name="phoneNumber"
                    type="tel"
                    value={editForm.phoneNumber}
                    onChange={handleInputChange}
                    placeholder="+1 (555) 123-4567"
                  />

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      onClick={handleEditToggle}
                      variant="secondary"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={loading}
                      loading={loading}
                      variant="primary"
                    >
                      Save Changes
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">
                        First Name
                      </label>
                      <p className="text-slate-900 font-medium">{displayData?.firstName || 'Not provided'}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">
                        Last Name
                      </label>
                      <p className="text-slate-900 font-medium">{displayData?.lastName || 'Not provided'}</p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">
                      Email Address
                    </label>
                    <p className="text-slate-900 font-medium">{displayData?.email || 'Not provided'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">
                      Phone Number
                    </label>
                    <p className="text-slate-900 font-medium">{displayData?.phoneNumber || 'Not provided'}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-500 mb-1">
                      User ID
                    </label>
                    <p className="text-slate-500 font-mono text-sm">{displayData?.id || 'Not available'}</p>
                  </div>

                  {displayData?.userName && (
                    <div>
                      <label className="block text-sm font-medium text-slate-500 mb-1">
                        Username
                      </label>
                      <p className="text-slate-900 font-medium">{displayData.userName}</p>
                    </div>
                  )}
                </div>
              )}
            </CardBody>
          </Card>
        </div>

        {/* JWT Debug Section */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader
              title="JWT Token Information"
              action={
                <Button
                  onClick={() => setShowJWTDebug(!showJWTDebug)}
                  variant="ghost"
                  size="small"
                >
                  {showJWTDebug ? 'Hide' : 'Show'} JWT Debug
                </Button>
              }
            />

            {showJWTDebug && jwtData && (
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">Token Expiration</h4>
                    <p className="text-sm text-slate-600">
                      {jwtData.tokenExpiration ? jwtData.tokenExpiration.toLocaleString() : 'Unknown'}
                    </p>
                  </div>

                  <div>
                    <h4 className="font-medium text-slate-700 mb-2">User ID from JWT</h4>
                    <p className="text-sm text-slate-600 font-mono bg-slate-50 px-2 py-1 rounded inline-block border border-slate-100">
                      {jwtData.user?.id || 'Not found'}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="font-medium text-slate-700 mb-2">Extracted User Data</h4>
                  <pre className="text-xs bg-slate-100 p-3 rounded-md overflow-auto max-h-40 border border-slate-200">
                    {JSON.stringify(jwtData.user, null, 2)}
                  </pre>
                </div>

                <details className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <summary className="p-3 bg-slate-50 cursor-pointer font-medium text-slate-700">
                    Raw JWT Claims
                  </summary>
                  <div className="p-3 bg-white border-t border-slate-100">
                    <pre className="text-xs bg-slate-100 p-3 rounded-md overflow-auto max-h-60">
                      {JSON.stringify(jwtData.decoded, null, 2)}
                    </pre>
                  </div>
                </details>

                <details className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                  <summary className="p-3 bg-slate-50 cursor-pointer font-medium text-slate-700">
                    Raw JWT Token
                  </summary>
                  <div className="p-3 bg-white border-t border-slate-100">
                    <textarea
                      readOnly
                      value={jwtData.rawToken}
                      className="w-full h-32 text-xs font-mono bg-slate-100 p-2 rounded border border-slate-200"
                    />
                  </div>
                </details>
              </div>
            )}
          </Card>
        </div>

        {/* Account Settings */}
        <div className="lg:col-span-3">
          <Card>
            <CardHeader title="Account Settings" />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-900">Security</h4>
                  <div className="space-y-3">
                    <Button variant="outline" fullWidth className="justify-start">
                      Change Password
                    </Button>
                    <Button variant="outline" fullWidth className="justify-start">
                      Two-Factor Authentication
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-slate-900">Preferences</h4>
                  <div className="space-y-3">
                    <Button variant="outline" fullWidth className="justify-start">
                      Notification Settings
                    </Button>
                    <Button variant="outline" fullWidth className="justify-start">
                      Privacy Settings
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  )
}

export default Profile